import { Injectable } from "@angular/core"
import { StructuredDataService } from "./StructuredData.service"
import { Article } from "app/models/Article"
import { ConvertToSlugPipe } from "./convert-to-slug.pipe";
import { environment } from "environments/environment";
import { Utilisateur } from "app/models/Utilisateur";


@Injectable({
  providedIn: "root",
})
export class ArticleSchemaService {
  private slugPipe = new ConvertToSlugPipe();
  
  constructor(private structuredDataService: StructuredDataService) {}

  /**
   * Generate and add ItemList schema for articles and news
   */
  generateArticleListSchema(listeArticles: Article[], listeNews: Article[], listeArticlesInfographie: Article[]): void {
    // Create the itemListElement array
    const itemListElements = [
      // Add 4 articles from listeArticles (or fewer if not enough)
      ...this.createListItems(listeArticles.slice(0, 4), "actualites/articles", 1),

      // Add 5 news from listeNews (or fewer if not enough)
      ...this.createListItems(
        listeNews.slice(0, 5),
        "actualites/news",
        listeArticles.length > 4 ? 5 : listeArticles.length + 1,
      ),

      // Add 1 article from listeArticlesInfographie (if available)
      ...this.createListItems(
        listeArticlesInfographie.slice(0, 1),
        "actualites/articles",
        listeArticles.length + listeNews.length + 1,
      ),
    ]

    // Create the complete ItemList schema
    const itemListSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": "https://irbe7.com/",
      },
      url: "https://irbe7.com/",
      numberOfItems: itemListElements.length.toString(),
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      itemListElement: itemListElements,
    }

    // Add the schema to the document
    this.structuredDataService.addSchema("ItemList", itemListSchema)
  }

  /**
   * Create list items for the ItemList schema
   */
  private createListItems(articles: Article[], baseUrl: string, startPosition: number): any[] {
    return articles.map((article, index) => {
      const position = startPosition + index
      const slug = this.slugPipe.transform(article.nom)

      return {
        "@type": "ListItem",
        position: position.toString(),
        url: `https://irbe7.com/${baseUrl}/${slug}/${article.id}`,
        name: article.nom,
      }
    })
  }
  generateArticleSchema(article: Article, user: Utilisateur): void {
    if (!article) return;

    const slug = this.slugPipe.transform(article.nom);
    const articleUrl = `${environment.apiUrl}/actualites/articles/${slug}/${article.id}`;
    const authorUrl = `${environment.apiUrl}/mur-utilisateur-autre/${user?.userId}`;

    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": articleUrl
      },
      "headline": article.nom,
      "name": article.nom,
      "description": article.description,
      "image": article.image ? [{
        "@type": "ImageObject",
        "url": environment.cloud_front_host + article.image,
        "width": 1200,
        "height": 675,
        "caption": article.nom
      }] : [],
      "author": {
        "@type": "Person",
        "name": user?.userName || "Anonymous",
        "url": authorUrl
      },
      "publisher": {
        "@type": "Organization",
        "name": "irbe7",
        "url": environment.apiUrl,
        "logo": {
          "@type": "ImageObject",
          "url": "https://d3b156xx8s447.cloudfront.net/Logo/LogoIrbe7",
          "width": 600,
          "height": 60
        },
        "sameAs": [
          "https://www.facebook.com/irbe7bourse",
          "https://www.youtube.com/@irbe7Business",
          " https://www.instagram.com/irbe7bourse"
        ]
      },
      "datePublished": article.date,
      "dateModified": article.date,
      "articleSection": article.themeArticle,
      "keywords": [article.themeArticle],
      "articleBody": article.contenu.replace(/<[^>]*>/g, ''),  // Remove all HTML tags, keep full content
      "inLanguage": "fr-TN",
      "isAccessibleForFree": "True"
    };

    this.structuredDataService.addSchema("NewsArticle", articleSchema);
  }
   generateNewsSchema(article: Article, user: Utilisateur): void {
    if (!article) return;

    const slug = this.slugPipe.transform(article.nom);
    const articleUrl = `${environment.apiUrl}/actualites/news/${slug}/${article.id}`;
    const authorUrl = `${environment.apiUrl}/mur-utilisateur-autre/${user?.userId}`;

    const newsSchema = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": articleUrl
      },
      "headline": article.nom,
      "name": article.nom,
      "description": article.description,
      "image": article.image ? [{
        "@type": "ImageObject",
        "url": environment.cloud_front_host + article.image,
        "width": 1200,
        "height": 675,
        "caption": article.nom
      }] : [],
      "author": {
        "@type": "Person",
        "name": user?.userName || "Anonymous",
        "url": authorUrl
      },
      "publisher": {
        "@type": "Organization",
        "name": "irbe7",
        "url": environment.apiUrl,
        "logo": {
          "@type": "ImageObject",
          "url": "https://d3b156xx8s447.cloudfront.net/Logo/LogoIrbe7",
          "width": 600,
          "height": 60
        },
        "sameAs": [
          "https://www.facebook.com/irbe7bourse",
          "https://www.youtube.com/@irbe7Business",
          " https://www.instagram.com/irbe7bourse"
        ]
      },
      "datePublished": article.date,
      "dateModified": article.date,
      "articleSection": article.themeArticle,
      "keywords": [article.themeArticle],
      "articleBody": article.contenu.replace(/<[^>]*>/g, ''),  // Remove all HTML tags, keep full content
      "inLanguage": "fr-TN",
      "isAccessibleForFree": "True"
    };

    this.structuredDataService.addSchema("News", newsSchema);
  }

generateGlobalWebPageSchema(pageData: {
  url: string;
  name: string;
  description:string;
  isPartOf?: {
    name: string;
    url: string;
  };
  inLanguage?: string;
}): void {
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": pageData.name,
      "description": pageData.description,
      "speakable": {
        "@type": "SpeakableSpecification",
        "url": pageData.url,
        "mainContentOfPage": "News",
        "language": "fr"
      },
      "isPartOf": {
      "@type": "WebSite",
      "name":"Irbe7",
      "url": "https://irbe7.com"
    },
    };

    this.structuredDataService.addSchema("WebPage", schema);
  }
}
